import React, { useState, useMemo } from 'react';
import {
  Box,
  Text,
  VStack,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { useUploadedData } from 'src/hooks/useUploadedData';
import { useAppContext } from 'src/contexts/AppContext';
import { UploadedDataResponse, UploadedFileInfo } from 'src/types';
import {
  getAvailableMetrics,
  getAvailableMediaSources,
  transformDataToDataSets,
} from 'src/features/DataUpload/utils/dataVisualizationHelpers';
import { ApprovedFilesBanner } from './components/ApprovedFilesBanner';
import { DataSetPanel } from './components/DataSetPanel';
import { DataVisualizationActions } from './components/DataVisualizationActions';
import { ErrorNotifBox } from 'src/components/atoms/ErrorNotifBox';

type ApprovedFile = {
  fileId: string;
  fileName: string;
  approvedAt: Date;
};

type DataVisualizationProps = {
  fileIds?: string[];
  showInternalLoading?: boolean;
  preloadedData?: UploadedDataResponse['data'] | null;
  onResetToUpload?: () => void;
  onApproveFiles?: (fileInfo: UploadedFileInfo) => void;
  approvedFiles?: ApprovedFile[];
  approvedFilesData?: UploadedDataResponse['data'] | null;
  uploadedFileNames?: string[];
  onUpload?: () => void;
  isPreviewMode?: boolean;
  isUploading?: boolean;
};
const DataVisualization: React.FC<DataVisualizationProps> = ({
  fileIds = [],
  showInternalLoading = true,
  preloadedData,
  onResetToUpload,
  onApproveFiles,
  approvedFiles = [],
  approvedFilesData,
  onUpload,
  isPreviewMode = false,
  isUploading = false,
}) => {
  const { currentCustomer } = useAppContext();
  const customerId = currentCustomer?.customerName || '';

  const {
    data: uploadedData,
    loading,
    error,
  } = useUploadedData({
    customerId,
    fileIds,
    dataType: 'all',
    enabled:
      !preloadedData && !!customerId && fileIds.length > 0 && !isPreviewMode,
  });

  // Use preloaded data if available, otherwise use fetched data
  const data = preloadedData || uploadedData;
  const isLoading = preloadedData ? false : loading;

  // Get file info from the current data to show file name
  const currentFileInfo = data?.fileInfo;

  // Check if current files are already approved
  const areCurrentFilesApproved = useMemo(() => {
    if (!currentFileInfo || approvedFiles.length === 0) return false;

    // If current file info has a combined filename (multiple files), check if all files are approved
    if (currentFileInfo.fileName?.includes(', ')) {
      const currentFileNames = currentFileInfo.fileName
        .split(', ')
        .map((name) => name.trim());
      return currentFileNames.every((fileName) =>
        approvedFiles.some(
          (approvedFile) => approvedFile.fileName === fileName,
        ),
      );
    } else {
      // Single file - check by fileId or fileName
      return approvedFiles.some(
        (file) =>
          file.fileId === currentFileInfo.fileId ||
          file.fileName === currentFileInfo.fileName,
      );
    }
  }, [currentFileInfo, approvedFiles]);

  // Combine data from current and approved files
  const combinedData = useMemo(() => {
    if (!data && !approvedFilesData) return null;

    // If we have both current and approved data, combine them
    if (data && approvedFilesData) {
      // Check if we're in preview mode and if the data is the same to avoid duplication
      if (
        isPreviewMode &&
        data.fileInfo?.fileId === approvedFilesData.fileInfo?.fileId
      ) {
        // Same data source, return current data to avoid duplication
        return data;
      }

      // Different data sources, combine them
      return {
        fileInfo: data.fileInfo, // Use current file info for display
        dailyData: [
          ...(data.dailyData || []),
          ...(approvedFilesData.dailyData || []),
        ],
        weeklyData: [
          ...(data.weeklyData || []),
          ...(approvedFilesData.weeklyData || []),
        ],
        monthlyData: [
          ...(data.monthlyData || []),
          ...(approvedFilesData.monthlyData || []),
        ],
        offlineData: [
          ...(data.offlineData || []),
          ...(approvedFilesData.offlineData || []),
        ],
        kpiData: [
          ...(data.kpiData || []),
          ...(approvedFilesData.kpiData || []),
        ],
        summary: {
          totalRecords:
            (data.summary?.totalRecords || 0) +
            (approvedFilesData.summary?.totalRecords || 0),
          dateRange: {
            earliest:
              data.summary?.dateRange?.earliest ||
              approvedFilesData.summary?.dateRange?.earliest,
            latest:
              data.summary?.dateRange?.latest ||
              approvedFilesData.summary?.dateRange?.latest,
          },
          dataTypes: [
            ...new Set([
              ...(data.summary?.dataTypes || []),
              ...(approvedFilesData.summary?.dataTypes || []),
            ]),
          ],
        },
      };
    }

    // If we only have current data, return it
    if (data && !approvedFilesData) {
      return data;
    }

    // If we only have approved files data, return it
    if (approvedFilesData && !data) {
      return approvedFilesData;
    }

    // Fallback: return whatever we have
    return data || approvedFilesData;
  }, [data, approvedFilesData, isPreviewMode]);

  // Available metrics for each data type
  const availableMetrics = useMemo(
    () => getAvailableMetrics(combinedData || null),
    [combinedData],
  );

  // Available media/sources for each data type
  const availableMediaSources = useMemo(
    () => getAvailableMediaSources(combinedData || null),
    [combinedData],
  );

  // Selected metrics state for each data type
  const [selectedMetrics, setSelectedMetrics] = useState<
    Record<string, string>
  >({
    daily: 'spend',
    weekly: 'spend',
    monthly: 'spend',
    offline: 'spend',
    kpi: 'sales',
  });

  // Selected media/source state for each data type
  const [selectedMediaSources, setSelectedMediaSources] = useState<
    Record<string, string>
  >({
    daily: 'all',
    weekly: 'all',
    monthly: 'all',
    offline: 'all',
  });

  // Handler for metric selection changes
  const handleMetricChange =
    (dataType: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedMetrics((prev) => ({
        ...prev,
        [dataType]: e.target.value,
      }));
    };

  // Handler for media/source selection changes
  const handleMediaSourceChange =
    (dataType: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedMediaSources((prev) => ({
        ...prev,
        [dataType]: e.target.value,
      }));
    };

  // Transform uploaded data into DataSet format for charts
  const dataSets = useMemo(
    () =>
      transformDataToDataSets(
        combinedData || null,
        selectedMetrics,
        selectedMediaSources,
      ),
    [combinedData, selectedMetrics, selectedMediaSources],
  );

  const [expandedItems, setExpandedItems] = useState<Set<string>>(
    new Set(dataSets.filter((d) => d.isExpanded).map((d) => d.id)),
  );

  const toggleItem = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  // Customer ID validation
  if (!customerId) {
    return (
      <Box w="100%" maxW="876px">
        <Alert status="warning">
          <AlertIcon />
          <AlertTitle>Customer not found</AlertTitle>
          <AlertDescription>
            Unable to load data. Please ensure you&apos;re logged in with a
            valid customer account.
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  // Loading state (only show if enabled)
  if (isLoading && showInternalLoading) {
    return (
      <Box
        w="100%"
        maxW="876px"
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="200px">
        <VStack>
          <Spinner size="lg" />
          <Text>Loading uploaded data...</Text>
        </VStack>
      </Box>
    );
  }

  // If loading but internal loading is disabled, show nothing (parent handles loading)
  if (isLoading && !showInternalLoading) {
    return null;
  }

  // Error state
  if (error) {
    return (
      <Box w="100%" maxW="876px">
        <ErrorNotifBox
          title="Error loading data!"
          description={typeof error === 'string' ? error : undefined}
        />
      </Box>
    );
  }

  // No data state
  if (!combinedData || dataSets.length === 0) {
    return (
      <Box w="100%" maxW="876px">
        <Alert status="info">
          <AlertIcon />
          <AlertTitle>No data found</AlertTitle>
          <AlertDescription>
            No data was found for this file. The file may not have been
            processed yet.
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <Box w="100%">
      <ApprovedFilesBanner approvedFiles={approvedFiles} />
      <VStack spacing={3} align="stretch">
        {dataSets.map((dataSet, index) => (
          <DataSetPanel
            key={dataSet.id}
            dataSet={dataSet}
            isExpanded={expandedItems.has(dataSet.id)}
            isLastItem={index === dataSets.length - 1}
            onToggle={toggleItem}
            availableMetrics={availableMetrics}
            availableMediaSources={availableMediaSources}
            selectedMetrics={selectedMetrics}
            selectedMediaSources={selectedMediaSources}
            onMetricChange={handleMetricChange}
            onMediaSourceChange={handleMediaSourceChange}
          />
        ))}
      </VStack>

      <DataVisualizationActions
        isPreviewMode={isPreviewMode}
        onResetToUpload={onResetToUpload}
        onUpload={onUpload}
        onApproveFiles={onApproveFiles}
        isUploading={Boolean(isUploading)}
        currentFileInfo={currentFileInfo}
        areCurrentFilesApproved={areCurrentFilesApproved}
      />
    </Box>
  );
};
export default DataVisualization;
